/**
 * Table showing self-service license links and their status
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Tooltip, message } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SelfServiceLink } from '../types';
import { selfServiceApi } from '../api';

const SelfServiceLinkTable: React.FC = () => {
  const [links, setLinks] = useState<SelfServiceLink[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await selfServiceApi.list();
      setLinks(response.results || []);
    } catch {
      message.error('Failed to load self-service links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const handleCopy = (url: string) => {
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    message.success('Link copied');
  };

  const columns: ColumnsType<SelfServiceLink> = [
    {
      title: 'Status',
      dataIndex: 'is_used',
      key: 'status',
      width: 90,
      render: (used: boolean) =>
        used ? <Tag color="default">Used</Tag> : <Tag color="green">Available</Tag>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: 'Redeemed By',
      key: 'redeemed',
      render: (_, record) => record.machine_company || '-',
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_username',
      key: 'created_by',
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Used At',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 160,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="Copy link">
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record.link_url)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={links}
      loading={loading}
      size="small"
      pagination={{ pageSize: 5, size: 'small' }}
      title={() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 500 }}>Self-Service Links</span>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchLinks}>
            Refresh
          </Button>
        </div>
      )}
    />
  );
};

export default SelfServiceLinkTable;
